import { VenueDetailsBySlugResponse, useI18N } from "common/hooks";
import OpeningHours from "@/components/VenueDetailSheet/components/OpeningHours";
import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowSquareOut, Clock, DeviceMobile, MapPin } from "phosphor-react";
import AnalyticsService from "@/services/analytics";
import VenueInfoButtons from "./VenueInfoButtons";

type VenueInfoAddressProps = {
  venueDetailsReponse: VenueDetailsBySlugResponse;
  fullSize?: boolean;
};

function VenueInfoAddress({
  venueDetailsReponse,
  fullSize
}: VenueInfoAddressProps) {
  const venueDetails = venueDetailsReponse.details;
  const i18n = useI18N();
  const venueTitle = useRef(null); // Reference for the venue name

  // Focus on the paragraph when the component mounts
  useEffect(() => {
    if (venueTitle.current) {
      venueTitle.current.focus();
    }
  }, []);

  return (
    <div className={`${fullSize ? "md:col-span-7" : "md:col-span-12"}`}>
      <header className="mb-4" role="none">
        <div className="flex flex-row justify-between float-right">
          {!fullSize && (
            <Link
              to={`/venuedetails/${venueDetails.business.slug}`}
              target="_blank"
              role="button"
              aria-label={i18n.get("openInNewTab")}
            >
              <p className="flex flex-row paragraph-small-semibold items-center !text-primary-500 dark:!text-primary-300">
                {i18n.get("openInNewTab")}
                <ArrowSquareOut size={16} className="ml-1" aria-hidden="true" />
              </p>
            </Link>
          )}
        </div>
      </header>

      <section role="none">
        <address className="text-primary-800 dark:text-primary-150 not-italic mb-4">
          <button
            onClick={() => {
              AnalyticsService.trackGAEventHandler("homeClickVenueAddress");
              AnalyticsService.trackMx("homeClickVenueAddress", {
                VenueId: venueDetails.business.id,
                VenueBrandId: venueDetails.brandId,
                VenueBrandName: venueDetails.brandName,
                VenueAddress: venueDetails.address,
                VenueName: venueDetails.name,
                VenueType: venueDetails.businessType,
              });

              const url = `https://www.google.com/maps/search/?api=1&query=${venueDetails.address}&query_place_id=${venueDetails.placeId}`;
              window.open(url, "_blank");
            }}
            className="mb-4 flex items-center gap-2"
            tabIndex={0}
            aria-label={`Address ${venueDetails.address}`}
          >
            <MapPin
              size={24}
              className="!text-primary-500 dark:!text-primary-300"
              aria-hidden="true"
            />
            <p
              className="paragraph-medium-medium text-left underline "
              aria-hidden="true"
            >
              {venueDetails.address}
            </p>
          </button>
          {venueDetails?.phone && (
            <div
              className="flex items-center gap-2 mb-4"
              tabIndex={0}
              role="text"
              aria-label={`Phone ${venueDetails.phone}`}
            >
              <DeviceMobile
                size={24}
                className="!text-primary-500 dark:!text-primary-300"
                aria-hidden="true"
              />
              <p
                className="paragraph-medium-medium underline"
                aria-hidden="true"
              >
                {venueDetails.phone}
              </p>
            </div>
          )}
          {venueDetails.openingHours &&
            venueDetails.openingHours[0]?.mode === 0 && (
              <div className="flex gap-2 paragraph-medium-medium">
                <Clock
                  size={24}
                  className="!text-primary-500 dark:!text-primary-300"
                />
                <OpeningHours
                  openingHours={venueDetails.openingHours}
                  containerClassName="cursor-pointer"
                />
              </div>
            )}
        </address>

        <VenueInfoButtons
          venueDetailsReponse={venueDetailsReponse}
          isLoadingTabs={false}
          hasNearbyVenues={false}
        />
      </section>
    </div>
  );
}

export default VenueInfoAddress;
